import { SafeError, SafePromise } from '@benzinga/safe-await';
import { ExtendedListenableSubscribable } from '@benzinga/subscribable';
import { Session } from '@benzinga/session';

import {
  Card,
  CheckoutSubscription,
  Coupon,
  Product,
  IPrecheckoutRequest,
  IPrecheckoutResponse,
  ILineItemPrice,
} from './entities';
import {
  ingressCard,
  ingressCards,
  ingressCheckoutSubscription,
  ingressCoupon,
  ingressProduct,
  ingressProducts,
} from './ingress';
import { CheckoutPost, ShopRestful } from './restful';

interface ErrorEvent {
  error?: SafeError;
  errorType:
    | 'product_error'
    | 'products_error'
    | 'coupon_error'
    | 'checkout_error'
    | 'get_credit_cards_error'
    | 'add_credit_cards_error'
    | 'get_credit_card_error'
    | 'cart_bail_error'
    | 'precheckout_error'
    | 'upgradePlan_error';
  type: 'error';
}

interface ProductEvent {
  products: Product;
  type: 'get_product';
}

interface ProductsEvent {
  products: Product[];
  type: 'get_products';
}

interface CouponsEvent {
  coupon: Coupon;
  type: 'get_coupon';
}

interface CheckoutEvent {
  coupon: CheckoutSubscription;
  type: 'post_checkout';
}

interface PreCheckoutEvent {
  precheckout: IPrecheckoutResponse;
  type: 'pre_checkout';
}

interface UpgradePlanEvent {
  plan: ILineItemPrice;
  type: 'upgrade_plan';
}

interface CardEvent {
  card: Card;
  type: 'get_credit_card' | 'add_credit_cards';
}

interface CardsEvent {
  cards: Card[];
  type: 'get_credit_cards';
}

interface CardsDeleteEvent {
  creditCardId: string;
  type: 'delete_credit_card';
}

interface CartBailEvent {
  type: 'cart_bailed';
}

export type ShopRequestEvent =
  | ErrorEvent
  | ProductEvent
  | ProductsEvent
  | CheckoutEvent
  | CouponsEvent
  | CardEvent
  | CardsEvent
  | CardsDeleteEvent
  | CartBailEvent
  | PreCheckoutEvent
  | UpgradePlanEvent;

interface ProductFunctions {
  getProducts: ShopRequest['getProducts'];
}

export class ShopRequest extends ExtendedListenableSubscribable<ShopRequestEvent, ProductFunctions> {
  private restful: ShopRestful;

  constructor(session: Session) {
    super();
    this.restful = new ShopRestful(session);
  }

  public getProduct = async (productName: string): SafePromise<Product> => {
    const products = await ingressProduct(this.restful.getProduct(productName));
    if (products.err) {
      this.dispatch({
        error: products.err,
        errorType: 'product_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        products: products.ok,
        type: 'get_product',
      });
    }
    return products;
  };

  public getProducts = async (): SafePromise<Product[]> => {
    const products = await ingressProducts(this.restful.getProducts());
    if (products.err) {
      this.dispatch({
        error: products.err,
        errorType: 'products_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        products: products.ok,
        type: 'get_products',
      });
    }
    return products;
  };

  public preCheckout = async (body: IPrecheckoutRequest): SafePromise<IPrecheckoutResponse> => {
    const products = await this.restful.preCheckout(body);
    if (products.err) {
      const jsonRes = await (products.err.data as Response).json();
      const json = jsonRes['error'];
      const error = { err: json ? new SafeError(`${json}`, 'response', products.err.data) : products.err };
      this.dispatch({
        error: error.err,
        errorType: 'precheckout_error',
        type: 'error',
      });
      return error;
    } else {
      this.dispatch({
        precheckout: products.ok,
        type: 'pre_checkout',
      });
    }
    return products;
  };

  public upgradePlan = async (
    selected_card: string,
    selected_plan: string,
    subscription_uuid: string,
  ): SafePromise<ILineItemPrice> => {
    const plan = await this.restful.upgradePlan(selected_card, selected_plan, subscription_uuid);
    if (plan.err) {
      this.dispatch({
        error: plan.err,
        errorType: 'upgradePlan_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        plan: plan.ok,
        type: 'upgrade_plan',
      });
    }
    return plan;
  };

  public getCouponsCode = async (code: string): SafePromise<Coupon> => {
    const products = await ingressCoupon(this.restful.getCouponsCode(code));
    if (products.err) {
      this.dispatch({
        error: products.err,
        errorType: 'coupon_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        coupon: products.ok,
        type: 'get_coupon',
      });
    }
    return products;
  };

  public checkout = async (body: CheckoutPost): SafePromise<CheckoutSubscription> => {
    const products = await ingressCheckoutSubscription(this.restful.postCheckout(body));
    if (products.err) {
      this.dispatch({
        error: products.err,
        errorType: 'checkout_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        coupon: products.ok,
        type: 'post_checkout',
      });
    }
    return products;
  };

  public cartBail = async (planSlug: string, submitted: boolean): SafePromise<undefined> => {
    const products = await this.restful.cartBail({ planSlug, submitted });
    if (products.err) {
      this.dispatch({
        error: products.err,
        errorType: 'cart_bail_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        type: 'cart_bailed',
      });
    }
    return products;
  };

  public getCreditCards = async (): SafePromise<Card[]> => {
    const products = await ingressCards(this.restful.getCreditCards());
    if (products.err) {
      this.dispatch({
        error: products.err,
        errorType: 'get_credit_cards_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        cards: products.ok,
        type: 'get_credit_cards',
      });
    }
    return products;
  };

  public addCreditCard = async (token: string): SafePromise<Card> => {
    const products = await ingressCard(this.restful.addCreditCard(token));
    if (products.err) {
      this.dispatch({
        error: products.err,
        errorType: 'add_credit_cards_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        card: products.ok,
        type: 'add_credit_cards',
      });
    }
    return products;
  };

  public getCreditCard = async (id: string): SafePromise<Card> => {
    const products = await ingressCard(this.restful.getCreditCard(id));
    if (products.err) {
      this.dispatch({
        error: products.err,
        errorType: 'get_credit_card_error',
        type: 'error',
      });
    } else {
      this.dispatch({
        card: products.ok,
        type: 'get_credit_card',
      });
    }
    return products;
  };

  public deleteCreditCard = async (id: string): SafePromise<undefined> => {
    const products = await this.restful.deleteCreditCard(id);
    if (products.err) {
      const jsonRes = await (products.err.data as Response).json();
      const json = jsonRes['error'];
      const error = { err: json ? new SafeError(`${json}`, 'response', products.err.data) : products.err };
      this.dispatch({
        error: error.err,
        errorType: 'get_credit_cards_error',
        type: 'error',
      });
      return error;
    } else {
      this.dispatch({
        creditCardId: id,
        type: 'delete_credit_card',
      });
    }
    return products;
  };

  protected onSubscribe(): ProductFunctions {
    return {
      getProducts: this.getProducts,
    };
  }
}
